import { NgModule } from '@angular/core';

import { ActivityProvider } from './domain/providers/activity.provider';
import { AdvisoryModelProvider } from './domain/providers/advisory-model-provider.service';
import { CityProvider } from './domain/providers/city.provider';
import { ContractProvider } from './domain/providers/contract.provider';
import { CountryProvider } from './domain/providers/country.provider';
import { ClientProvider } from './domain/providers/client.provider';
import { DictionaryProvider } from './domain/providers/dictionary.provider';
import { DocumentProvider } from './domain/providers/document.provider';
import { LegalProvider } from './domain/providers/legal.provider';
import { NormativeCountryProvider } from './domain/providers/normative-country.provider';
import { OperationProvider } from './domain/providers/operation.provider';
import { OrderProvider } from './domain/providers/order.provider';
import { ProductProvider } from './domain/providers/product.provider';
import { ProfessionalPositionProvider } from './domain/providers/professional-position.provider';
import { OtherProfessionalPositionProvider } from './domain/providers/other-professional-position.provider';
import { SessionProvider } from './domain/providers/session.provider';
import { StreetTypeProvider } from './domain/providers/street-type.provider';
import { RoleNameProvider } from './domain/providers/role-name.provider';
import { TaxClassificationsProvider } from './domain/providers/tax-classifications.provider';
import { TestProvider } from './domain/providers/test.provider';
import { TraderProvider } from './domain/providers/trader.provider';
import { TransferProvider } from './domain/providers/transfer-provider.service';
import { UserProvider } from './domain/providers/user.provider';
import { ValidationProvider } from './domain/providers/validation.provider';

import { ActivityApiRepository } from './domain/repositories/activity-api.repository';
import { ActivityCacheRepository } from './domain/repositories/activity-cache.repository';
import { AdvisoryModelApiRepository } from './domain/repositories/advisory-model-api.repository';
import { AdvisoryModelCacheRepository } from './domain/repositories/advisory-model-cache.repository';
import { CheckApiRepository } from './domain/repositories/check-api.repository';
import { CheckCacheRepository } from './domain/repositories/check-cache.repository';
import { CityApiRepository } from './domain/repositories/city-api.repository';
import { CityCacheRepository } from './domain/repositories/city-cache.repository';
import { ContractApiRepository } from './domain/repositories/contract-api.repository';
import { CountryApiRepository } from './domain/repositories/country-api.repository';
import { CountryCacheRepository } from './domain/repositories/country-cache.repository';
import { ContractCacheRepository } from './domain/repositories/contract-cache.repository';
import { ClientApiRepository } from './domain/repositories/client-api.repository';
import { ClientCacheRepository } from './domain/repositories/client-cache.repository';
import { DictionaryApiRepository } from './domain/repositories/dictionary-api.repository';
import { DictionaryCacheRepository } from './domain/repositories/dictionary-cache.repository';
import { DocumentApiRepository } from './domain/repositories/document-api.repository';
import { FullClientApiRepository } from './domain/repositories/full-client-api.repository';
import { FullClientCacheRepository } from './domain/repositories/full-client-cache.repository';
import { LegalApiRepository } from './domain/repositories/legal-api.repository';
import { LegalCacheRepository } from './domain/repositories/legal-cache.repository';
import { NormativeCountryApiRepository } from './domain/repositories/normative-country-api.repository';
import { NormativeCountryCacheRepository } from './domain/repositories/normative-country-cache.repository';
import { OrderApiRepository } from './domain/repositories/order-api.repository';
import { ProductApiRepository } from './domain/repositories/product-api.repository';
import { ProductCacheRepository } from './domain/repositories/product-cache.repository';
import { ProductDetailCacheRepository } from './domain/repositories/product-detail-cache.repository';
import { ProfessionalPositionApiRepository } from './domain/repositories/professional-position-api.repository';
import { OtherProfessionalPositionApiRepository } from './domain/repositories/other-professional-position-api.repository';
import { ProfessionalPositionCacheRepository } from './domain/repositories/professional-position-cache.repository';
import { OtherProfessionalPositionCacheRepository } from './domain/repositories/other-professional-position-cache.repository';
import { SelectedProductCacheRepository } from './domain/repositories/selected-product-cache.repository';
import { SessionApiRepository } from './domain/repositories/session-api.repository';
import { StreetTypeApiRepository } from './domain/repositories/street-type-api.repository';
import { StreetTypeCacheRepository } from './domain/repositories/street-type-cache.repository';
import { RoleNameApiRepository } from './domain/repositories/role-name-api.repository';
import { RoleNameCacheRepository } from './domain/repositories/role-name-cache.repository';
import { TaxClassificationsApiRepository } from './domain/repositories/tax-classifications-api.repository';
import { TaxClassificationsCacheRepository } from './domain/repositories/tax-classifications-cache.repository';
import { TestApiRepository } from './domain/repositories/test-api.repository';
import { TraderApiRepository } from './domain/repositories/trader-api.repository';
import { TraderCacheRepository } from './domain/repositories/trader-cache.repository';
import { TransferApiRepository } from './domain/repositories/transfer-api.repository';
import { UserApiRepository } from './domain/repositories/user-api.repository';
import { UserCacheRepository } from './domain/repositories/user-cache.repository';
import { ValidationRepository } from './domain/repositories/validation.repository';

import { ContractService } from './domain/services/contract.service';

import { AcceptOrderSignatoriesUseCase } from './domain/usecases/accept-order-signatories.use-case';
import { AcceptTestSignatoriesUseCase } from './domain/usecases/accept-test-signatories.use-case';
import { AddSelectedProductRefundUseCase } from './domain/usecases/add-selected-product-refund.use-case';
import { AddParticipantUseCase } from './domain/usecases/add-participant.use-case';
import { AddSelectedProductsUseCase } from './domain/usecases/add-selected-products.use-case';
import { CancelContractUseCase } from './domain/usecases/cancel-contract.use-case';
import { CheckClientUseCase } from './domain/usecases/check-client.use-case';
import { CheckEmailUseCase } from './domain/usecases/check-email.use-case';
import { CheckPhoneUseCase } from './domain/usecases/check-phone.use-case';
import { ContinueOrderSignUseCase } from './domain/usecases/continue-order-sign.use-case';
import { ContinueTestSignUseCase } from './domain/usecases/continue-test-sign.use-case';
import { CreateContractUseCase } from './domain/usecases/create-contract.use-case';
import { CreateContractWithProductsUseCase } from './domain/usecases/create-contract-with-products.use-case';
import { CreateOperationUseCase } from './domain/usecases/create-operation.use-case';
import { EvaluateFeeUseCase } from './domain/usecases/evaluate-fee.use-case';
import { FinalizeContractUseCase } from './domain/usecases/finalize-contract.use-case';
import { GetActivitiesUseCase } from './domain/usecases/get-activities.use-case';
import { GetAdvisoryModelsUseCaseService } from './domain/usecases/get-advisory-models-use-case.service';
import { GetAppropriatenessClientUseCase } from './domain/usecases/get-appropriateness-client.use-case';
import { GetCitiesUseCase } from './domain/usecases/get-cities.use-case';
import { GetClientUseCase } from './domain/usecases/get-client.use-case';
import { GetClientByDocumentUseCase } from './domain/usecases/get-client-by-document.use-case';
import { GetContractDataUseCase } from './domain/usecases/get-contract-data.use-case';
import { GetContractSignatoriesUseCase } from './domain/usecases/get-contract-signatories.use-case';
import { GetContractsUseCase } from './domain/usecases/get-contracts.use-case';
import { GetContractUseCase } from './domain/usecases/get-contract.use-case';
import { GetCountriesUseCase } from './domain/usecases/get-countries.use-case';
import { GetDictionaryUseCase } from './domain/usecases/get-dictionary.use-case';
import { GetDocumentContentUseCase } from './domain/usecases/get-document-content.use-case';
import { GetExternalTradersUseCase } from './domain/usecases/get-external-traders.use-case';
import { GetFirstHolderUseCase } from './domain/usecases/get-first-holder.use-case';
import { GetMyProductsUseCase } from './domain/usecases/get-my-products.use-case';
import { GetLegalDisclaimerUseCase } from './domain/usecases/get-legal-disclaimer.use-case';
import { GetNormativeCountriesUseCase } from './domain/usecases/get-normative-countries.use-case';
import { GetOperationUseCase } from './domain/usecases/get-operation.use-case';
import { GetOptimalSelectedProductUseCase } from './domain/usecases/get-optimal-selected-product.use-case';
import { GetOrderDocumentsUseCase } from './domain/usecases/get-order-documents.use-case';
import { GetParticipantsUseCase } from './domain/usecases/get-participants.use-case';
import { GetProductUseCase } from './domain/usecases/get-product.use-case';
import { GetProductsUseCase } from './domain/usecases/get-products.use-case';
import { GetProfessionalPositionsUseCase } from './domain/usecases/get-professional-positions.use-case';
import { GetOtherProfessionalPositionsUseCase } from './domain/usecases/get-other-professional-positions.use-case';
import { GetProfileUseCase } from './domain/usecases/get-profile.use-case';
import { GetPupilsUseCase } from './domain/usecases/get-pupils.use-case';
import { GetSelectedProductSignatoriesUseCase } from './domain/usecases/get-selected-product-signatories.use-case';
import { GetSelectedProductsUseCase } from './domain/usecases/get-selected-products.use-case';
import { GetSessionUseCase } from './domain/usecases/get-session.use-case';
import { GetSignatoriesUseCase } from './domain/usecases/get-signatories.use-case';
import { GetStreetTypesUseCase } from './domain/usecases/get-street-types.use-case';
import { GetRoleNameUseCase } from './domain/usecases/get-role-name.use-case';
import { GetTaxClassificationsUseCase } from './domain/usecases/get-tax-classifications.use-case';
import { GetTestUseCase } from './domain/usecases/get-test.use-case';
import { GetTestResultsUseCase } from './domain/usecases/get-test-results.use-case';
import { GetTestDocumentsUseCase } from './domain/usecases/get-test-documents.use-case';
import { GetTradersUseCase } from './domain/usecases/get-traders.use-case';
import { GetTutorsUseCase } from './domain/usecases/get-tutors.use-case';
import { GetValidateClientNotificationsUseCase } from './domain/usecases/get-validate-client-notifications.use-case';
import { GetValidModelsUseCase } from './domain/usecases/get-valid-models-use-case.service';
import { GetModelPdfUseCase } from './domain/usecases/get-model-pdf.use-case';
import { RememberOrderUseCase } from './domain/usecases/remember-order-use-case';
import { RemoveDocumentUseCase } from './domain/usecases/remove-document.use-case';
import { RemoveOrderUseCase } from './domain/usecases/remove-order.use-case';
import { RemoveParticipantUseCase } from './domain/usecases/remove-participant.use-case';
import { RemoveSelectedProductsUseCase } from './domain/usecases/remove-selected-products.use-case';
import { SetFeeUseCase } from './domain/usecases/set-fee.use-case';
import { SetTransferUseCase } from './domain/usecases/set-transfer-use-case.use-case';
import { SignDisinvestmentUseCase } from './domain/usecases/sign-disinvestment.use-case';
import { SignModelUseCase } from './domain/usecases/sign-model.use-case';
import { SignOrderUseCase } from './domain/usecases/sign-order.use-case';
import { SignTestUseCase } from './domain/usecases/sign-test.use-case';
import { SignTransferUseCase } from './domain/usecases/sign-transfer.use-case';
import { UpdateClientUseCase } from './domain/usecases/update-client.use-case';
import { UpdateContractUseCase } from './domain/usecases/update-contract.use-case';
import { UpdateFirstHolderUseCase } from './domain/usecases/update-first-holder.use-case';
import { UpdateOperationUseCase } from './domain/usecases/update-operation.use-case';
import { UpdateOrderUseCase } from './domain/usecases/update-order.use-case';
import { UpdateParticipantUseCase } from './domain/usecases/update-participant.use-case';
import { UpdateSelectedProductsUseCase } from './domain/usecases/update-selected-products.use-case';
import { UpdateSessionUseCase } from './domain/usecases/update-session.use-case';
import { UpdateTestUseCase } from './domain/usecases/update-test.use-case';
import { UpdateValidationUseCase } from './domain/usecases/update-validation.use-case';
import { UploadOrderDocumentsUseCase } from './domain/usecases/upload-order-documents-use-case';
import { UploadTestDocumentsUseCase } from './domain/usecases/upload-test-documents-use-case';
import { ValidateClientDataUseCase } from './domain/usecases/validate-client-data.use-case';
import { ValidateContractUseCase } from './domain/usecases/validate-contract.use-case';
import { ValidateContractOnlyUseCase } from './domain/usecases/validate-contract-only.use-case';
import { GetProductsByOrderTypeUseCase,GetProductsByOrderTypeClientUseCase } from './domain/usecases/get-products-by-order-type.use-case';
import { ProductByOrderTypeCacheRepository } from './domain/repositories/productbyordertype-cache.repository';

const APP_PROVIDERS = [
    ActivityProvider,
    AdvisoryModelProvider,
    CityProvider,
    ContractProvider,
    CountryProvider,
    ClientProvider,
    DictionaryProvider,
    DocumentProvider,
    LegalProvider,
    NormativeCountryProvider,
    OperationProvider,
    OrderProvider,
    TransferProvider,
    ProductProvider,
    ProfessionalPositionProvider,
    OtherProfessionalPositionProvider,
    SessionProvider,
    StreetTypeProvider,
    RoleNameProvider,
    TaxClassificationsProvider,
    TestProvider,
    TraderProvider,
    UserProvider,
    ValidationProvider,

    ActivityApiRepository,
    ActivityCacheRepository,
    AdvisoryModelApiRepository,
    AdvisoryModelCacheRepository,
    CheckApiRepository,
    CheckCacheRepository,
    CityApiRepository,
    CityCacheRepository,
    ContractApiRepository,
    CountryApiRepository,
    CountryCacheRepository,
    ContractCacheRepository,
    ClientApiRepository,
    ClientCacheRepository,
    DictionaryApiRepository,
    DictionaryCacheRepository,
    DocumentApiRepository,
    FullClientApiRepository,
    FullClientCacheRepository,
    LegalApiRepository,
    LegalCacheRepository,
    NormativeCountryApiRepository,
    NormativeCountryCacheRepository,
    OrderApiRepository,
    TransferApiRepository,
    ProductApiRepository,
    ProductCacheRepository,
    ProductDetailCacheRepository,
    ProfessionalPositionApiRepository,
    OtherProfessionalPositionApiRepository,
    ProfessionalPositionCacheRepository,
    OtherProfessionalPositionCacheRepository,
    SelectedProductCacheRepository,
    SessionApiRepository,
    StreetTypeApiRepository,
    StreetTypeCacheRepository,
    RoleNameApiRepository,
    RoleNameCacheRepository,
    TaxClassificationsApiRepository,
    TaxClassificationsCacheRepository,
    TestApiRepository,
    TraderApiRepository,
    TraderCacheRepository,
    TransferApiRepository,
    UserApiRepository,
    UserCacheRepository,
    ValidationRepository,

    ContractService,

    AcceptOrderSignatoriesUseCase,
    AcceptTestSignatoriesUseCase,
    AddParticipantUseCase,
    AddSelectedProductRefundUseCase,
    AddSelectedProductsUseCase,
    CancelContractUseCase,
    CheckClientUseCase,
    CheckEmailUseCase,
    CheckPhoneUseCase,
    ContinueOrderSignUseCase,
    ContinueTestSignUseCase,
    CreateContractUseCase,
    CreateContractWithProductsUseCase,
    CreateOperationUseCase,
    EvaluateFeeUseCase,
    FinalizeContractUseCase,
    GetActivitiesUseCase,
    GetAdvisoryModelsUseCaseService,
    GetAppropriatenessClientUseCase,
    GetCitiesUseCase,
    GetClientUseCase,
    GetClientByDocumentUseCase,
    GetContractDataUseCase,
    GetContractSignatoriesUseCase,
    GetContractsUseCase,
    GetContractUseCase,
    GetCountriesUseCase,
    GetDictionaryUseCase,
    GetDocumentContentUseCase,
    GetExternalTradersUseCase,
    GetFirstHolderUseCase,
    GetLegalDisclaimerUseCase,
    GetMyProductsUseCase,
    GetNormativeCountriesUseCase,
    GetOperationUseCase,
    GetOptimalSelectedProductUseCase,
    GetOrderDocumentsUseCase,
    GetParticipantsUseCase,
    GetModelPdfUseCase,
    GetProductUseCase,
    GetProductsUseCase,
    GetProfessionalPositionsUseCase,
    GetOtherProfessionalPositionsUseCase,
    GetProfileUseCase,
    GetPupilsUseCase,
    GetSelectedProductSignatoriesUseCase,
    GetSelectedProductsUseCase,
    GetSessionUseCase,
    GetSignatoriesUseCase,
    GetStreetTypesUseCase,
    GetRoleNameUseCase,
    GetTaxClassificationsUseCase,
    GetTestUseCase,
    GetTestResultsUseCase,
    GetTestDocumentsUseCase,
    GetTradersUseCase,
    GetTutorsUseCase,
    GetValidateClientNotificationsUseCase,
    GetValidModelsUseCase,
    RememberOrderUseCase,
    RemoveDocumentUseCase,
    RemoveOrderUseCase,
    RemoveParticipantUseCase,
    RemoveSelectedProductsUseCase,
    SetFeeUseCase,
    SetTransferUseCase,
    SignDisinvestmentUseCase,
    SignModelUseCase,
    SignOrderUseCase,
    SignTestUseCase,
    SignTransferUseCase,
    UpdateClientUseCase,
    UpdateContractUseCase,
    UpdateFirstHolderUseCase,
    UpdateOperationUseCase,
    UpdateOrderUseCase,
    UpdateParticipantUseCase,
    UpdateSelectedProductsUseCase,
    UpdateSessionUseCase,
    UpdateTestUseCase,
    UpdateValidationUseCase,
    UploadOrderDocumentsUseCase,
    UploadTestDocumentsUseCase,
    ValidateClientDataUseCase,
    ValidateContractUseCase,
    ValidateContractOnlyUseCase,

    GetProductsByOrderTypeUseCase,
    GetProductsByOrderTypeClientUseCase,


    ProductByOrderTypeCacheRepository];

@NgModule({
    id: 'core-module',
    providers: APP_PROVIDERS,
})
export class CoreModule {}
